import { ArrowLeft, FileText, AlertCircle } from "lucide-react";
import { BottomNav } from "./bottom-nav";

interface TermsPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export function TermsPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: TermsPageProps) {
  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Termos de Uso</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Last Updated */}
        <div className="bg-secondary/30 border border-primary/20 rounded-xl p-4 mb-6">
          <div className="flex gap-3">
            <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-foreground mb-1">Última Atualização</h3>
              <p className="text-sm text-muted-foreground">19 de Janeiro de 2026</p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="prose prose-sm max-w-none mb-6">
          <p className="text-sm text-muted-foreground leading-relaxed mb-4">
            Bem-vindo ao akihub. Ao utilizar nosso aplicativo e serviços, você concorda 
            com os seguintes termos e condições. Por favor, leia atentamente antes de usar a plataforma.
          </p>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {/* Section 1 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">1. Aceitação dos Termos</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              Ao criar uma conta e utilizar o akihub, você declara ter lido, compreendido 
              e concordado com estes Termos de Uso, bem como com nossa Política de Privacidade.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Reservamo-nos o direito de modificar estes termos a qualquer momento. Alterações 
              significativas serão comunicadas por e-mail ou notificação no aplicativo.
            </p>
          </section>

          {/* Section 2 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">2. Elegibilidade e Cadastro</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Você deve ter pelo menos 18 anos de idade para usar o akihub</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>As informações fornecidas durante o cadastro devem ser verdadeiras e precisas</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Você é responsável por manter a confidencialidade de sua senha</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Apenas uma conta por pessoa física é permitida</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Contas comerciais devem possuir CNPJ válido</span>
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">3. Uso da Plataforma</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              <strong className="text-foreground">Permitido:</strong>
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground mb-4">
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Comprar e vender produtos usados em bom estado</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Comunicar-se respeitosamente com outros usuários</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Publicar anúncios de produtos legítimos</span>
              </li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              <strong className="text-foreground">Proibido:</strong>
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-destructive flex-shrink-0">✕</span>
                <span>Vender produtos falsificados, roubados ou ilegais</span>
              </li>
              <li className="flex gap-2">
                <span className="text-destructive flex-shrink-0">✕</span>
                <span>Realizar transações fora da plataforma para evitar taxas</span>
              </li>
              <li className="flex gap-2">
                <span className="text-destructive flex-shrink-0">✕</span>
                <span>Publicar conteúdo ofensivo, discriminatório ou enganoso</span>
              </li>
              <li className="flex gap-2">
                <span className="text-destructive flex-shrink-0">✕</span>
                <span>Manipular avaliações ou criar contas falsas</span>
              </li>
              <li className="flex gap-2">
                <span className="text-destructive flex-shrink-0">✕</span>
                <span>Fazer spam ou enviar mensagens não solicitadas</span>
              </li>
            </ul>
          </section>

          {/* Section 4 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">4. Anúncios e Listagens</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Vendedores são responsáveis pela precisão das descrições dos produtos</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Fotos devem representar fielmente o produto anunciado</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Anúncios podem ser removidos se violarem nossas políticas</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Reservamo-nos o direito de moderar e remover conteúdo inadequado</span>
              </li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">5. Transações e Pagamentos</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>O akihub facilita transações mas não é parte da compra/venda</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Compradores devem efetuar pagamento conforme acordado</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Vendedores devem entregar produtos conforme anunciado</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Taxas de serviço aplicam-se conforme plano escolhido</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Reembolsos seguem nossa política de devolução</span>
              </li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">6. Propriedade Intelectual</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Todo o conteúdo do aplicativo, incluindo design, logotipos, textos e código-fonte, 
              são propriedade do akihub ou de seus licenciadores e estão protegidos por leis 
              de direitos autorais e propriedade intelectual brasileiras e internacionais.
            </p>
          </section>

          {/* Section 7 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">7. Limitação de Responsabilidade</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>O akihub não garante a qualidade, segurança ou legalidade dos produtos</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Não somos responsáveis por perdas decorrentes de transações entre usuários</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Usuários interagem por sua própria conta e risco</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Não garantimos disponibilidade ininterrupta do serviço</span>
              </li>
            </ul>
          </section>

          {/* Section 8 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">8. Suspensão e Encerramento</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              Podemos suspender ou encerrar sua conta imediatamente, sem aviso prévio, se você:
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Violar estes Termos de Uso</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Praticar fraude ou atividades ilegais</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Receber múltiplas reclamações de outros usuários</span>
              </li>
              <li className="flex gap-2">
                <span className="text-primary flex-shrink-0">•</span>
                <span>Abusar da plataforma ou de outros usuários</span>
              </li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">9. Lei Aplicável e Foro</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Estes Termos de Uso são regidos pelas leis da República Federativa do Brasil. 
              Qualquer disputa será resolvida no foro da comarca de São Paulo, SP, com exclusão 
              de qualquer outro, por mais privilegiado que seja.
            </p>
          </section>

          {/* Section 10 */}
          <section className="bg-card border border-border rounded-xl p-4">
            <h2 className="font-medium text-foreground mb-3">10. Contato</h2>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">
              Para dúvidas sobre estes Termos de Uso, entre em contato:
            </p>
            <div className="bg-secondary/30 rounded-lg p-3">
              <p className="text-sm text-foreground">
                <strong>E-mail:</strong> legal@akihub.io
              </p>
            </div>
          </section>
        </div>

        {/* Warning Notice */}
        <div className="mt-6 bg-secondary/30 border border-primary/20 rounded-xl p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-foreground mb-2">Aviso Importante</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Ao continuar usando o akihub, você confirma que leu, compreendeu e 
                concordou com estes Termos de Uso. Se você não concordar com algum termo, 
                por favor, não utilize nossos serviços.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}